import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import type { BoardShare, UUID } from "@/lib/types";

// Read + manage the workspaces a board is shared into. Sharing/unsharing changes
// who can reach the board, so it invalidates every board-list surface (my-boards,
// pins, and per-workspace lists) alongside the shares query itself.
export function useBoardShares(boardId: UUID | undefined, enabled = true) {
  const qc = useQueryClient();
  const key = boardId ? queryKeys.boardShares(boardId) : ["boards", "void", "shares"];

  const list = useQuery({
    enabled: Boolean(boardId) && enabled,
    queryKey: key,
    queryFn: () => api.listBoardShares(boardId as UUID),
  });

  function invalidateReach() {
    void qc.invalidateQueries({ queryKey: queryKeys.boardShares(boardId as UUID) });
    void qc.invalidateQueries({ queryKey: queryKeys.myBoards });
    void qc.invalidateQueries({ queryKey: queryKeys.pinnedBoards });
    // Covers the workspace list + every per-workspace board grid + members.
    void qc.invalidateQueries({ queryKey: ["workspaces"] });
  }

  const share = useMutation<void, Error, UUID>({
    mutationFn: (workspaceId) => api.shareBoard(boardId as UUID, workspaceId),
    onSuccess: invalidateReach,
  });

  const unshare = useMutation<void, Error, UUID>({
    mutationFn: (workspaceId) => api.unshareBoard(boardId as UUID, workspaceId),
    onSuccess: invalidateReach,
  });

  const shares: BoardShare[] = list.data ?? [];
  return { shares, isLoading: list.isLoading, share, unshare };
}
